<script lang="ts">
    import {getDrawerStore, getToastStore, Tab, TabGroup} from '@skeletonlabs/skeleton';
    import {ClientStatuses, CreateClientSteps} from '$lib/models/client/types';
    import Summary from '$lib/components/drawers/client/create/tabs/Summary.svelte';
    import type {IdName} from '$lib/models/types';
    import {onMount} from 'svelte';
    import {aliiExportTypeStore} from '$lib/stores/aliiExportTypeStore';
    import {aliiConnectTypeStore} from '$lib/stores/aliiConnectTypeStore';
    import {createClientStore} from '$lib/stores/createClientStore';
    import InitialUser from '$lib/components/drawers/client/create/tabs/InitialUser.svelte';
    import Permissions from '$lib/components/drawers/client/create/tabs/Permissions.svelte';
    import ClientDetails from '$lib/components/drawers/client/create/tabs/ClientDetails.svelte';
    import {clientStore} from '$lib/stores/clientStore';
    import {get} from 'svelte/store';
    import {DataHandler} from '@vincjo/datatables/remote';
    import IconButton from '$lib/components/common/iconButton/IconButton.svelte';
    import {UsersThree} from 'phosphor-svelte';
    import Button from '$lib/components/common/button/Button.svelte';

    export let dataHandler: DataHandler;

  let exportTypes: IdName[] = [];
  let connectionTypes: IdName[] = [];

  const isLoading = createClientStore.isLoading;
  const toastrStore = getToastStore();
  const drawerStore = getDrawerStore();

  onMount(async () => {
    connectionTypes = await aliiConnectTypeStore.get();
    exportTypes = await aliiExportTypeStore.get();

    createClientStore.setClientStatus(ClientStatuses.find((idName) => idName.name.toLowerCase() === 'active')?.id ?? undefined);
    createClientStore.setConnectionTypeId(connectionTypes.find((idName) => idName.name.toLowerCase() === 'none')?.id ?? undefined);
    createClientStore.setExportTypeId(exportTypes.find((idName) => idName.name.toLowerCase() === 'none')?.id ?? undefined);

    createClientStore.isLoading.set(false);
  });

  let tabSet = createClientStore.currentStep;
  $: disablePreviousButton = $tabSet === CreateClientSteps.ClientDetails;
  $: displayNext = $tabSet < CreateClientSteps.Review;

  let enableNextButton = createClientStore.currentStepValid;

  let maxStep = createClientStore.maxValidStep;
  $: reviewStepValid = $maxStep >= CreateClientSteps.Review;
  $: initialUserStepValid = $maxStep >= CreateClientSteps.InitialUser;
  $: permissionStepValid = $maxStep >= CreateClientSteps.Permission;

  function onPreviousClick() {
    if ($tabSet > 0) {
      createClientStore.currentStep.set($tabSet - 1);
    }
  }

  function onCloseClick() {
    drawerStore.close();
  }

  async function onProgressClick() {
    if (displayNext) {
      if ($tabSet === CreateClientSteps.ClientDetails) {
        createClientStore.applyOrgDefault();
      }
      createClientStore.currentStep.set($tabSet + 1);
    } else {
      try {
        await clientStore.create(get(createClientStore.request));
        drawerStore.close();
        dataHandler.invalidate();
        createClientStore.reset();
      } catch (errors) {
        for (let error of errors) {
          toastrStore.trigger({
            message: `${error.key} - ${error.value}`,
            background: 'bg-error-500',
            classes: 'text-white',
          });
        }
      }
    }
  }
</script>

<div class="card flex h-full flex-col">
  <header class="flex justify-between border-b p-5">
    <div class="inline-flex items-center gap-2 p-4 text-2xl font-bold">
      <span>
        <UsersThree />
      </span>
      <span> Create Client </span>
    </div>
    <div class="content-center">
      <IconButton icon="x" onClick={onCloseClick} />
    </div>
  </header>
  <div class="flex-grow overflow-y-auto p-4">
    {#if $isLoading}
      <div>Loading...</div>
    {:else}
      <div>
        <TabGroup border="border-b">
          <Tab bind:group={$tabSet} name="clientDetail" value={CreateClientSteps.ClientDetails}>
            <div class="flex items-center gap-1">
              <span>Client Details</span>
            </div>
          </Tab>
          <Tab
            bind:group={$tabSet}
            name="Initial User"
            hover={!initialUserStepValid ? '!cursor-not-allowed' : 'hover:variant-soft'}
            value={CreateClientSteps.InitialUser}
            disabled={!initialUserStepValid}
          >
            <div class="flex items-center gap-1">
              <span>Initial User</span>
            </div>
          </Tab>
          <Tab
            bind:group={$tabSet}
            name="permissions"
            hover={!reviewStepValid ? '!cursor-not-allowed' : 'hover:variant-soft'}
            value={CreateClientSteps.Permission}
            disabled={!permissionStepValid}
          >
            <div class="flex items-center gap-1">
              <span>Permission</span>
            </div>
          </Tab>
          <Tab
            bind:group={$tabSet}
            name="review"
            hover={!permissionStepValid ? '!cursor-not-allowed' : 'hover:variant-soft'}
            value={CreateClientSteps.Review}
            disabled={!reviewStepValid}
          >
            <div class="flex items-center gap-1">
              <span>Review</span>
            </div>
          </Tab>
          <svelte:fragment slot="panel">
            {#if $tabSet === CreateClientSteps.ClientDetails}
              <ClientDetails {exportTypes} {connectionTypes} />
            {:else if $tabSet === CreateClientSteps.InitialUser}
              <InitialUser />
            {:else if $tabSet === CreateClientSteps.Permission}
              <Permissions />
            {:else if $tabSet === CreateClientSteps.Review}
              <Summary {exportTypes} {connectionTypes} />
            {/if}
          </svelte:fragment>
        </TabGroup>
      </div>
    {/if}
  </div>
  <footer class="flex w-full justify-between gap-5 border-t p-4">
    <Button size="large" colour="error" label="Cancel" variant="outlined" onClick={onCloseClick} />
    <div class="inline-flex flex-wrap gap-5">
      <Button size="large" colour="error" label="Previous Step" disabled={disablePreviousButton} onClick={onPreviousClick} />
      <Button
        size="large"
        colour={displayNext ? 'secondary' : 'primary'}
        label={displayNext ? 'Next Step' : 'Create Client'}
        disabled={!$enableNextButton}
        onClick={onProgressClick}
      />
    </div>
  </footer>
</div>
